import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { validatePhoneNumber, cleanPhoneNumber } from '../utils/validation';

const API_URL = process.env.REACT_APP_API_URL;

const PatientRegistration = ({ patient, onSaved, onCancel }) => {
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    dob: null,
    phone: '',
    email: '',
    address: '',
    height: '',
    weight: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    if (patient) {
      setFormData({
        name: patient.name || '',
        gender: patient.gender || '',
        dob: patient.dob ? new Date(patient.dob) : null,
        phone: patient.phone || '',
        email: patient.email || '',
        address: patient.address || '',
        height: patient.height || '',
        weight: patient.weight || ''
      });
    }
  }, [patient]);

  const handleChange = (field, value) => {
    if (field === 'phone') {
      // Only allow digits and common phone separators
      const cleanedValue = value.replace(/[^\d\s\-\(\)]/g, '');
      setFormData({ ...formData, [field]: cleanedValue });
      
      // Validate phone number
      const validation = validatePhoneNumber(cleanedValue);
      if (!validation.isValid && cleanedValue.length > 0) {
        setPhoneError(validation.error);
      } else {
        setPhoneError('');
      }
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number before submission
    const phoneValidation = validatePhoneNumber(formData.phone);
    if (!phoneValidation.isValid) {
      setPhoneError(phoneValidation.error);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const requestData = {
        ...formData,
        dob: formData.dob ? formData.dob.toISOString().split('T')[0] : null,
        phone: phoneValidation.cleanPhone // Use cleaned phone number
      };

      // Debug: Log authentication info
      console.log('Auth Status:', { isAuthenticated, user, token: localStorage.getItem('token') });

      if (patient) {
        // Update existing patient
        await axios.put(`${API_URL}/api/patients/${patient.id}`, requestData);
      } else {
        // Register new patient
        await axios.post(`${API_URL}/api/patients/register`, requestData);
      }

      onSaved();
    } catch (err) {
      console.error('Registration error:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Failed to save patient');
    } finally {
      setLoading(false);
    }
  };

  // Show authentication status for debugging
  if (!isAuthenticated) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        You must be logged in to register patients. Please log in as an admin or receptionist.
      </Alert>
    );
  }

  if (user && user.role !== 'ADMIN' && user.role !== 'RECEPTIONIST') {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        You need ADMIN or RECEPTIONIST role to register patients. Current role: {user.role}
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        {/* Debug info */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Logged in as: {user?.name} (Role: {user?.role})
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Gender</InputLabel>
              <Select
                value={formData.gender}
                onChange={(e) => handleChange('gender', e.target.value)}
                label="Gender"
              >
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Date of Birth"
              value={formData.dob}
              onChange={(date) => handleChange('dob', date)}
              renderInput={(params) => <TextField {...params} fullWidth required />}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              error={!!phoneError}
              helperText={phoneError || 'Enter 10-digit phone number'}
              required
              inputProps={{
                maxLength: 15, // Allow for formatting characters
                placeholder: '1234567890'
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Height (cm)"
              type="number"
              value={formData.height}
              onChange={(e) => handleChange('height', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Weight (kg)"
              type="number"
              value={formData.weight}
              onChange={(e) => handleChange('weight', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={3}
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {patient ? 'Update Patient' : 'Register Patient'}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default PatientRegistration; 